<script setup lang="ts" generic="TInternal, TExternal">
import { computed, reactive } from 'vue'

/**
* Map data between a local state (v-model) and an internal isomorphic representation (scoped `state.value`).
*
* - Use `value` to directly access a readonly value
* - Use `state.value` to access the writable internal state
*
* Examples:
* - Use the existence of an Url parameter as the model for the visibility of a Modal
* - Add annotations to a boolean to represent it in a Button (aria-pressed vs. aria-expanded vs. aria-selected)
* - `./Sync.vue` is implemented using `Map` - it accepts a composable to be used as the model
*
* In addition, it can be extended with [State Decorator](https://en.wikipedia.org/wiki/Decorator_pattern) features:
* - clear
* - reset
* - toggle
*
* - Validation logic
* - Formatting
* - Side effects
*/
const props = defineProps<{
  in: (external: TExternal) => TInternal
  out: (internal: TInternal) => TExternal
}>()

const external = defineModel<TExternal>({ required: true })

const internal = computed({
  get: () => props.in(external.value),
  set: (internal: TInternal) => {
    external.value = props.out(internal)
  }
})

// This creates an object where 'value' is linked to 'internal'
const state = reactive({
  value: internal
})
</script>

<template>
  <slot :state :value="state.value" />
</template>
